/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react'
import { Tooltip, Button, CircularProgress } from '@material-ui/core'
import SettingsVoiceIcon from '@material-ui/icons/SettingsVoice'
import { BRAND_COLOR, getAppColors } from 'lib/constants'

type Props = {
  disabled?: boolean
  connecting?: boolean
  sending?: boolean
  connectionFailed?: boolean
  /** Called when the user clicks the Reconnect button (shown 3s after
   *  connectionFailed becomes true). If omitted, reconnect UI is hidden. */
  onReconnect?: () => void
  lastError?: string
  busyHoldMs?: number
  onStart: () => void
  onStop: () => void
  canPTT?: boolean
  title?: string
  width?: number | string
  minWidth?: number | string
  height?: number | string
  borderRadius?: number | string
  style?: React.CSSProperties
  className?: string
  disabledTooltip?: string
}

const RECONNECT_REVEAL_MS = 3000

export default function PTTButton({
  disabled = false,
  connecting = false,
  sending = false,
  connectionFailed = false,
  onReconnect,
  lastError,
  busyHoldMs = 900,
  onStart,
  onStop,
  canPTT = true,
  title,
  width,
  minWidth = 140,
  height = 40,
  borderRadius = 20,
  style,
  className,
  disabledTooltip,
}: Props) {
  const [pressed, setPressed] = useState(false)
  const [busyFlash, setBusyFlash] = useState(false)
  const [coolingDown, setCoolingDown] = useState(false)
  const cooldownTimerRef = useRef<number | null>(null)

  const lastPointerDownRef = useRef(0)
  const DEBOUNCE_MS = 150

  // After connectionFailed flips true, hold the "Connection failed" label
  // for RECONNECT_REVEAL_MS, then swap to a clickable Reconnect button.
  const [showReconnect, setShowReconnect] = useState(false)
  useEffect(() => {
    if (!connectionFailed) {
      setShowReconnect(false)
      return
    }
    setShowReconnect(false)
    const t = window.setTimeout(() => setShowReconnect(true), RECONNECT_REVEAL_MS)
    return () => clearTimeout(t)
  }, [connectionFailed])

  const inReconnectMode = connectionFailed && showReconnect && Boolean(onReconnect)

  useEffect(() => {
    const isBusyErr = typeof lastError === 'string' && lastError.toLowerCase().includes('busy')
    if (isBusyErr) {
      setBusyFlash(true)
      setPressed(false)

      const t = window.setTimeout(() => setBusyFlash(false), busyHoldMs)
      return () => clearTimeout(t)
    }
  }, [lastError, busyHoldMs])

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current)
        cooldownTimerRef.current = null
      }
    }
  }, [])

  const busyNow = busyFlash
  // In reconnect mode the button is enabled and clickable (no PTT).
  // Otherwise, the usual PTT-disable rules apply.
  const actuallyDisabled = inReconnectMode
    ? false
    : sending
    ? false
    : disabled || connecting || connectionFailed || !canPTT || coolingDown || busyNow

  const pressedRef = useRef(false)
  const capturedIdRef = useRef<number | null>(null)
  const startedOnceRef = useRef(false)

  const startCooldown = () => {
    setCoolingDown(true)
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current)
    }
    cooldownTimerRef.current = window.setTimeout(() => {
      setCoolingDown(false)
      cooldownTimerRef.current = null
    }, 1000)
  }

  const stopIfActive = () => {
    if (!pressedRef.current) return
    pressedRef.current = false

    if (startedOnceRef.current) {
      startedOnceRef.current = false
      setPressed(false)
      onStop()
      startCooldown()
    }

    capturedIdRef.current = null
  }

  const onPointerDown: React.PointerEventHandler<HTMLButtonElement> = e => {
    if (actuallyDisabled) return

    const now = Date.now()
    if (now - lastPointerDownRef.current < DEBOUNCE_MS) {
      return
    }
    lastPointerDownRef.current = now

    if (busyFlash) setBusyFlash(false)

    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    capturedIdRef.current = e.pointerId

    if (!pressedRef.current) {
      pressedRef.current = true
      startedOnceRef.current = true
      setPressed(true)
      onStart()
    }
  }

  const onPointerUp: React.PointerEventHandler<HTMLButtonElement> = e => {
    if (capturedIdRef.current !== null && e.pointerId !== capturedIdRef.current) return
    e.preventDefault()
    stopIfActive()
  }

  const onPointerCancel: React.PointerEventHandler<HTMLButtonElement> = e => {
    if (capturedIdRef.current !== null && e.pointerId !== capturedIdRef.current) return
    e.preventDefault()
    stopIfActive()
  }

  const onContextMenu: React.MouseEventHandler = e => e.preventDefault()

  const label = inReconnectMode
    ? 'Reconnect'
    : connectionFailed
    ? 'Connection failed'
    : connecting
    ? 'Connecting…'
    : sending
    ? 'Talking…'
    : busyNow
    ? 'Busy'
    : coolingDown
    ? 'Push To Talk'
    : title || 'Push To Talk'

  const background = busyNow
    ? getAppColors('--color-gray-3')
    : actuallyDisabled
    ? getAppColors('--color-gray-3')
    : sending
    ? BRAND_COLOR
    : getAppColors('--color-surface')

  const color = busyNow ? '#9ca3af' : actuallyDisabled ? '#9ca3af' : sending ? '#ffffff' : BRAND_COLOR

  const borderColor = busyNow
    ? getAppColors('--color-gray-1')
    : actuallyDisabled
    ? getAppColors('--color-gray-1')
    : BRAND_COLOR

  const tooltip = (() => {
    if (busyNow) return 'Channel busy'
    if (inReconnectMode) return 'Click to reconnect'
    if (connectionFailed) return 'Connection failed'
    if (actuallyDisabled && disabledTooltip) return disabledTooltip

    if (actuallyDisabled) {
      return connecting
        ? 'Connecting…'
        : coolingDown
        ? 'Wait a second…'
        : !canPTT
        ? 'Mic not ready'
        : 'Join a channel to talk'
    }

    return sending ? 'Talking…' : 'Hold to talk'
  })()

  const handleReconnectClick = () => {
    if (!inReconnectMode) return
    onReconnect?.()
  }

  return (
    <Tooltip title={tooltip}>
      <span>
        <Button
          className={className}
          onPointerDown={inReconnectMode ? undefined : onPointerDown}
          onPointerUp={inReconnectMode ? undefined : onPointerUp}
          onPointerCancel={inReconnectMode ? undefined : onPointerCancel}
          onContextMenu={onContextMenu}
          onClick={inReconnectMode ? handleReconnectClick : undefined}
          startIcon={
            inReconnectMode ? (
              <SettingsVoiceIcon />
            ) : connecting ? (
              <CircularProgress size={14} thickness={5} style={{ color }} />
            ) : (
              <SettingsVoiceIcon />
            )
          }
          disabled={actuallyDisabled}
          variant="outlined"
          style={{
            touchAction: 'none',
            width,
            minWidth,
            height,
            borderRadius,
            border: `2px solid ${borderColor}`,
            backgroundColor: background,
            color,
            fontWeight: 600,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            display: 'inline-flex',
            alignItems: 'center',
            opacity: pressed ? 0.96 : 1,
            ...style,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span>{label}</span>
          </span>
        </Button>
      </span>
    </Tooltip>
  )
}
